import React, { useState } from 'react';
import { UgcAdScene } from '../../services/api/ugcAdService';
import { UgcAdSceneDraft } from './types';
import GenerationProgress from './GenerationProgress';
import { TaskyButton, TaskyTextarea, TaskyEmptyState, TaskyDialog, TaskyTooltip, useConfirm } from '../tasky';
import { ProductImageSelectionModal } from '../image-generation';
import {
  ImageIcon,
  RefreshCw,
  Loader2,
  AlertCircle,
  Sparkles,
  Save,
  GripVertical,
  Trash2,
  FolderOpen,
  Wand2,
  ArrowRight,
} from 'lucide-react';

/**
 * ステップ③: ストーリーボード（song-ad StoryboardStep 踏襲）。
 *
 * ②構成案でフィックスしたシーンごとに、開始フレームとなる静止画（絵コンテ）を生成する。
 * 9:16縦カードのグリッド。DnD並び替え（ドロップ時に即PUT scenes）・素材差し替え・
 * シーン単位AI修正・削除・失敗時の再試行。セリフはここでも編集できる
 * （ローカルドラフト+「設定を保存」でまとめてPUT）。
 *
 * シーンの追加は②構成案に置き、ここには置かない（字コンテ・セリフが空のシーンを
 * ここで足すと絵コンテ生成の材料が無いため。song-ad との意図的な差分）。
 */

interface StoryboardStepProps {
  scenes: UgcAdScene[];
  sceneDrafts: UgcAdSceneDraft[];
  generatingSceneIds: Set<string>;
  isSavingDrafts: boolean;
  isReordering?: boolean;
  onGenerate: (sceneId: string) => void;
  onGenerateAll: () => void;
  /** 一括生成の実行中。ボタンを無効化して連打による重複リクエストを防ぐ */
  isGeneratingAll?: boolean;
  /** 429（同時実行上限）直後のクールダウン中。生成ボタンをしばらく押せなくする */
  generationCoolingDown?: boolean;
  onSceneDialogueChange: (sceneId: string, text: string) => void;
  onSaveDrafts: () => Promise<void>;
  /** ドラッグ&ドロップで確定した新しい並び順（シーンid配列）。呼び出し側で即PUTする */
  onReorder: (orderedSceneIds: string[]) => void;
  onDeleteScene: (sceneId: string) => void;
  onAiEditScene: (sceneId: string, instruction: string) => Promise<void>;
  onReplaceSceneImage: (sceneId: string, s3Key: string) => Promise<void>;
  onProceedToMotion?: () => void;
}

const AI_EDIT_SUGGESTIONS = ['商品をもっと大きくする', '人物の表情を明るくする', '背景を生活感のある部屋にする', 'スマホで撮ったような質感にする', '画面をシンプルにする'];

/** 絵コンテ生成の処理段階（クライアント側の演出。backendは細かい進捗を返さない） */
const STORYBOARD_STAGES = [
  '構図を決めています',
  '登場人物を配置しています',
  '絵コンテを描いています',
  '仕上げています',
];

const StoryboardStep: React.FC<StoryboardStepProps> = ({
  scenes,
  sceneDrafts,
  generatingSceneIds,
  isSavingDrafts,
  isReordering = false,
  onGenerate,
  onGenerateAll,
  isGeneratingAll = false,
  generationCoolingDown = false,
  onSceneDialogueChange,
  onSaveDrafts,
  onReorder,
  onDeleteScene,
  onAiEditScene,
  onReplaceSceneImage,
  onProceedToMotion,
}) => {
  const confirm = useConfirm();
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [assetSwapSceneId, setAssetSwapSceneId] = useState<string | null>(null);
  const [aiEditSceneId, setAiEditSceneId] = useState<string | null>(null);
  const [aiInstruction, setAiInstruction] = useState('');
  const [isAiSubmitting, setIsAiSubmitting] = useState(false);

  if (scenes.length === 0) {
    return (
      <TaskyEmptyState
        icon={<ImageIcon />}
        title="先に構成案を確定してください"
        description="「② 構成案」で字コンテとセリフを確定するとストーリーボードを生成できます"
      />
    );
  }

  const pendingScenes = scenes.filter(
    (s) => s.storyboard.status === 'none' || s.storyboard.status === 'failed'
  );
  const activeCount = scenes.filter(
    (s) => s.storyboard.status === 'pending' || s.storyboard.status === 'processing'
  ).length;
  const allReady = scenes.every((s) => !!s.storyboard.image);

  const handleDrop = (targetId: string) => {
    setDragOverId(null);
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const order = scenes.map((s) => s.id);
    const from = order.indexOf(dragId);
    const to = order.indexOf(targetId);
    if (from < 0 || to < 0) {
      setDragId(null);
      return;
    }
    const next = [...order];
    next.splice(from, 1);
    next.splice(to, 0, dragId);
    setDragId(null);
    onReorder(next);
  };

  const aiEditScene = scenes.find((s) => s.id === aiEditSceneId);

  const handleAiEditSubmit = async () => {
    if (!aiEditSceneId || !aiInstruction.trim()) return;
    setIsAiSubmitting(true);
    try {
      await onAiEditScene(aiEditSceneId, aiInstruction.trim());
      setAiEditSceneId(null);
      setAiInstruction('');
    } finally {
      setIsAiSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-600">
          セリフの編集は「セリフを保存」で反映されます。構図や場面を変えるときは②で字コンテを直してください。
        </p>
        <div className="flex items-center gap-2">
          <TaskyButton variant="outline" size="sm" onClick={() => onSaveDrafts()} isLoading={isSavingDrafts}>
            <Save className="w-3.5 h-3.5" />
            セリフを保存
          </TaskyButton>
          <TaskyButton
            size="sm"
            onClick={onGenerateAll}
            isLoading={isGeneratingAll}
            disabled={pendingScenes.length === 0 || isGeneratingAll || generationCoolingDown}
          >
            {!isGeneratingAll && <Sparkles className="w-3.5 h-3.5" />}
            {isGeneratingAll ? '生成を開始しています…' : `未生成を一括生成（${pendingScenes.length}件）`}
          </TaskyButton>
        </div>
      </div>

      {/* 何を処理していて、あと何件かを出す（UGC-02） */}
      <GenerationProgress
        active={activeCount > 0}
        stages={STORYBOARD_STAGES}
        estimate="1枚あたり30秒ほどかかります"
        counts={{ done: scenes.length - activeCount, total: scenes.length }}
        className="mb-4"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {scenes.map((scene, index) => {
          const status = scene.storyboard.status;
          const isRequesting = generatingSceneIds.has(scene.id);
          const isActive = status === 'pending' || status === 'processing' || isRequesting;
          const draft = sceneDrafts.find((d) => d.id === scene.id);
          const isDragOver = dragOverId === scene.id && dragId !== scene.id;

          return (
            <div
              key={scene.id}
              draggable
              onDragStart={() => setDragId(scene.id)}
              onDragEnd={() => {
                setDragId(null);
                setDragOverId(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverId(scene.id);
              }}
              onDragLeave={() => setDragOverId((cur) => (cur === scene.id ? null : cur))}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(scene.id);
              }}
              className={`border rounded-xl overflow-hidden bg-white transition-shadow ${
                isDragOver ? 'border-primary ring-2 ring-primary/30' : 'border-warm-border'
              } ${dragId === scene.id ? 'opacity-50' : ''}`}
            >
              <div className="aspect-[9/16] bg-gray-100 relative flex items-center justify-center">
                {scene.storyboard.image ? (
                  <img
                    src={scene.storyboard.image.url}
                    alt={`シーン${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                ) : isActive ? (
                  <div className="flex flex-col items-center text-gray-400">
                    <Loader2 className="w-7 h-7 animate-spin mb-2" />
                    <span className="text-xs">生成中...</span>
                  </div>
                ) : status === 'failed' ? (
                  <div className="flex flex-col items-center text-red-400 px-4 text-center">
                    <AlertCircle className="w-7 h-7 mb-2" />
                    <span className="text-xs mb-2">{scene.storyboard.error_message ?? '生成に失敗しました'}</span>
                    <button
                      type="button"
                      onClick={() => onGenerate(scene.id)}
                      disabled={generationCoolingDown}
                      className="text-xs text-white bg-destructive hover:opacity-90 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50"
                    >
                      再試行
                    </button>
                  </div>
                ) : (
                  <ImageIcon className="w-9 h-9 text-gray-300" />
                )}
                <span className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 text-white text-[10px] font-semibold rounded-full px-2 py-0.5">
                  <GripVertical className="w-3 h-3 cursor-grab" />
                  シーン {index + 1}
                  {scene.beat ? ` ・${scene.beat}` : ''}
                </span>
              </div>

              <div className="p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[11px] text-gray-400">約{Math.round(scene.duration_sec || 0)}秒</span>
                  <div className="flex items-center">
                    <TaskyTooltip
                      content={scene.storyboard_prompt ? '画像を再生成' : '②で構成案を確定すると生成できます'}
                    >
                      <button
                        onClick={() => onGenerate(scene.id)}
                        disabled={isActive || !scene.storyboard_prompt || generationCoolingDown}
                        title={scene.storyboard_prompt ? '画像を再生成' : '②で構成案を確定すると生成できます'}
                        aria-label="画像を再生成"
                        className="p-1.5 text-gray-400 hover:text-primary transition-colors disabled:opacity-40"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isRequesting ? 'animate-spin' : ''}`} />
                      </button>
                    </TaskyTooltip>
                    <TaskyTooltip content="素材に差し替え">
                      <button
                        onClick={() => setAssetSwapSceneId(scene.id)}
                        disabled={isActive}
                        title="素材に差し替え"
                        className="p-1.5 text-gray-400 hover:text-primary transition-colors disabled:opacity-40"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                      </button>
                    </TaskyTooltip>
                    <TaskyTooltip content="AIで修正">
                      <button
                        onClick={() => setAiEditSceneId(scene.id)}
                        disabled={isActive}
                        title="AIで修正"
                        className="p-1.5 text-gray-400 hover:text-primary transition-colors disabled:opacity-40"
                      >
                        <Wand2 className="w-3.5 h-3.5" />
                      </button>
                    </TaskyTooltip>
                    <TaskyTooltip content={scenes.length <= 2 ? 'シーンは2つ以上必要です' : '削除'}>
                      <button
                        onClick={async () => {
                          if (scenes.length <= 2) return;
                          if (await confirm({ title: `シーン${index + 1}を削除しますか？残りのシーンで尺を自動再配分します。`, tone: 'destructive', confirmText: '削除する' })) {
                            onDeleteScene(scene.id);
                          }
                        }}
                        disabled={scenes.length <= 2}
                        title={scenes.length <= 2 ? 'シーンは2つ以上必要です' : '削除'}
                        className="p-1.5 text-gray-400 hover:text-destructive transition-colors disabled:opacity-40"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </TaskyTooltip>
                  </div>
                </div>

                <label className="block text-[11px] text-gray-500 mb-0.5">セリフ・ナレーション</label>
                <TaskyTextarea
                  value={draft?.dialogue ?? ''}
                  onChange={(e) => onSceneDialogueChange(scene.id, e.target.value)}
                  rows={3}
                  className="text-xs"
                />

                {scene.direction && (
                  <details className="mt-1.5">
                    <summary className="text-[11px] text-gray-400 cursor-pointer select-none">映像イメージ（字コンテ）</summary>
                    <p className="text-[11px] text-gray-500 mt-1 whitespace-pre-wrap">{scene.direction}</p>
                  </details>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {onProceedToMotion && (
        <div className="flex justify-end mt-5">
          <TaskyButton onClick={onProceedToMotion} disabled={!allReady}>
            モーション設定へ
            <ArrowRight className="w-4 h-4" />
          </TaskyButton>
        </div>
      )}

      {/* 素材差し替えモーダル */}
      {assetSwapSceneId && (
        <ProductImageSelectionModal
          isOpen
          maxSelection={1}
          onClose={() => setAssetSwapSceneId(null)}
          onSelect={(selected) => {
            const sceneId = assetSwapSceneId;
            setAssetSwapSceneId(null);
            if (sceneId && selected[0]) {
              void onReplaceSceneImage(sceneId, selected[0].s3Key);
            }
          }}
        />
      )}

      {/* AI修正モーダル */}
      <TaskyDialog
        open={!!aiEditSceneId}
        onClose={() => {
          setAiEditSceneId(null);
          setAiInstruction('');
        }}
        title={aiEditScene ? `AIでシーン${scenes.indexOf(aiEditScene) + 1}を修正` : 'AIで修正'}
        footer={
          <>
            <TaskyButton
              variant="outline"
              onClick={() => {
                setAiEditSceneId(null);
                setAiInstruction('');
              }}
            >
              キャンセル
            </TaskyButton>
            <TaskyButton onClick={handleAiEditSubmit} isLoading={isAiSubmitting} disabled={!aiInstruction.trim()}>
              <Wand2 className="w-4 h-4" />
              修正する
            </TaskyButton>
          </>
        }
      >
        <TaskyTextarea
          value={aiInstruction}
          onChange={(e) => setAiInstruction(e.target.value)}
          rows={2}
          placeholder="例：商品をもっと大きくして、背景を明るくする"
          className="mb-3"
        />
        <div className="flex flex-wrap gap-1.5">
          {AI_EDIT_SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setAiInstruction((prev) => (prev ? `${prev}、${s}` : s))}
              className="rounded-full px-3 py-1.5 text-xs text-gray-600 border border-warm-border hover:border-primary transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      </TaskyDialog>
    </div>
  );
};

export default StoryboardStep;
